<template>
  <div v-if="renderToDom" :class="popoverClasses" :style="popoverStyle">
    <div ref="box" class="popover__box">
      <button v-if="showCloseButton" type="button" class="icon-btn icon-btn--50 btn-close" @click="$emit('close')">
        <sd-icon name="close" />
      </button>
      <slot name="body">
        <p class="has-font-75"><slot></slot></p>
      </slot>
    </div>
  </div>
</template>

<script>
import "@smartdesign/core/scss/components/popover.scss";
import sdIcon from "../icon/icon.vue";
export default {
  components: {
    sdIcon,
  },

  props: {
    /**
     * set position: `bottom`, `right` (right automatically gets bottom if there is not enough space on the right side)
     */
    position: {
      required: false,
      type: String,
      default: "bottom",
      validator: (val) => {
        return ["bottom", "right"].indexOf(val) !== -1;
      },
    },

    show: {
      required: false,
      type: Boolean,
      default: false,
    },

    showCloseButton: {
      required: false,
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      renderToDom: this.show,
      addShowCssClass: this.show,
      thisElementDOMRect: null,
      boxElementDOMRect: null,
      targetElementDOMRect: null,
      // DOMRect = { height: 123, width: 123, x: 123, y: 123 }
      overriddenMaxHeight: null,
      windowInnerWidth: null,
    };
  },

  computed: {
    popoverClasses() {
      return {
        popover: true,
        "popover--is-shown": this.addShowCssClass,
        "popover--bottom": this.calculatedPosition === "bottom",
        "popover--right": this.calculatedPosition === "right",
      };
    },
    popoverStyle() {
      return {
        left: this.calculatedLeft,
        top: this.calculatedTop,
        "max-height": this.overriddenMaxHeight || this.calculatedMaxHeight,
      };
    },

    calculatedPosition() {
      const target = this.targetElementDOMRect;
      if (target && this.position === "right" && this.windowInnerWidth > target.x + target.width + 200) {
        return "right";
      }
      return "bottom";
    },

    calculatedTop() {
      const target = this.targetElementDOMRect;
      const popover = this.thisElementDOMRect;
      if (target && popover && this.calculatedPosition === "right") {
        return `${Math.round(target.y + target.height / 2 - popover.height / 2)}px`;
      }
      return undefined;
    },

    calculatedLeft() {
      const target = this.targetElementDOMRect;
      if (target && this.calculatedPosition === "right") {
        return `${target.x + target.width}px`;
      }
      return undefined;
    },

    calculatedMaxHeight() {
      const box = this.boxElementDOMRect;
      if (box && this.calculatedPosition === "bottom") {
        return `${box.height}px`;
      }
      return undefined;
    },
  },

  watch: {
    async show() {
      return this.show ? this.animateToShow() : this.animateToHide();
    },
  },

  methods: {
    async animateToHide() {
      this.addShowCssClass = false;
      if (this.calculatedPosition === "bottom") {
        this.overriddenMaxHeight = "0px";
      }

      // remove from dom after animation is finished
      await new Promise((resolve) => setTimeout(() => resolve(), 500));
      this.renderToDom = false;
      this.overriddenMaxHeight = null;
    },

    async animateToShow() {
      this.addShowCssClass = false;
      this.renderToDom = true;

      await this.$nextTick();

      this.measureDistances();
      this.overriddenMaxHeight = "0px";

      // render one frame to have a change in css and actually play the animation
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      this.addShowCssClass = true;
      this.overriddenMaxHeight = null;
    },

    measureDistances() {
      const thisIndex = this.$parent?.$children?.reduce((prev, cur, index) => (cur === this ? index : prev));
      if (thisIndex && thisIndex > 0) {
        this.targetElementDOMRect = this.$parent.$children[thisIndex - 1].$el.getBoundingClientRect();
      }
      this.thisElementDOMRect = this.$el?.getBoundingClientRect ? this.$el.getBoundingClientRect() : null;
      this.boxElementDOMRect = this.$refs?.box?.getBoundingClientRect ? this.$refs.box.getBoundingClientRect() : null;
      this.windowInnerWidth = window.innerWidth;
    },
  },

  mounted() {
    this.measureDistances();
  },
};
</script>
